import { useState, useEffect, useRef } from "react";
import { Send, Search, MessageCircle, ArrowLeft, User } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { ProtectedRoute } from "../components/ProtectedRoute";
import api from "../lib/api";

const MessagesContent = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (stored) setUser(JSON.parse(stored));
    fetchConversations();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeConv) {
      pollRef.current = setInterval(() => fetchMessages(activeConv.conversation_id, true), 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConversations = async () => {
    try {
      const res = await api.get("/messages/conversations");
      setConversations(res.data || []);
    } catch (err) { console.error('Error:', err.message || err); }
    setLoading(false);
  };

  const fetchMessages = async (convId, silent = false) => {
    try {
      const res = await api.get(`/messages/${convId}`);
      setMessages(res.data || []);
      if (!silent) fetchConversations();
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const openConversation = (conv) => {
    setActiveConv(conv);
    fetchMessages(conv.conversation_id);
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !activeConv) return;
    const toId = activeConv.other_user?.user_id;
    if (!toId) return;
    try {
      await api.post("/messages/send", { to_user_id: toId, content: newMsg });
      setNewMsg("");
      fetchMessages(activeConv.conversation_id);
      fetchConversations();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send");
    }
  };

  const startConversation = async (targetUser) => {
    setSearchQuery("");
    setSearchResults([]);
    const existing = conversations.find(c => c.other_user?.user_id === targetUser.user_id);
    if (existing) {
      openConversation(existing);
    } else {
      const fakeConv = {
        conversation_id: `conv_${[user.user_id, targetUser.user_id].sort().join("_")}`,
        other_user: targetUser,
        last_message: "",
        unread_count: 0
      };
      setActiveConv(fakeConv);
      setMessages([]);
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data || []);
    } catch (err) { console.error('Error:', err.message || err); }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="messages-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-12 py-6">
        <h1 className="font-['Outfit'] text-3xl font-bold text-[#0F172A] mb-6">Messages</h1>
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" style={{ height: "calc(100vh - 180px)" }}>
          <div className="flex h-full">
            {/* Sidebar */}
            <div className={`w-full md:w-80 border-r border-slate-100 flex flex-col ${activeConv ? "hidden md:flex" : "flex"}`}>
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="user-search-input"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9 h-10 rounded-xl bg-slate-50 border-slate-200 text-sm"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.map((u) => (
                      <button key={u.user_id} onClick={() => startConversation(u)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors" data-testid={`search-user-${u.user_id}`}>
                        {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white text-xs font-bold">{u.name?.[0]}</div>}
                        <span className="text-sm font-medium text-slate-700">{u.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 && !loading ? (
                  <div className="p-8 text-center">
                    <MessageCircle className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">No conversations yet</p>
                    <p className="text-xs text-slate-300 mt-1">Search for users to start chatting</p>
                  </div>
                ) : conversations.map((c) => (
                  <button key={c.conversation_id} onClick={() => openConversation(c)} data-testid={`conv-${c.conversation_id}`} className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors border-b border-slate-50 ${activeConv?.conversation_id === c.conversation_id ? "bg-[#F2F9F6]" : "hover:bg-slate-50"}`}>
                    {c.other_user?.picture ? <img src={c.other_user.picture} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" /> : <div className="h-10 w-10 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{c.other_user?.name?.[0] || "?"}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#0F172A] truncate">{c.other_user?.name}</p>
                        {c.unread_count > 0 && <span className="bg-[#0B6E4F] text-white text-xs rounded-full px-2 py-0.5 font-bold">{c.unread_count}</span>}
                      </div>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!activeConv ? "hidden md:flex" : "flex"}`}>
              {activeConv ? (
                <>
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                    <button onClick={() => setActiveConv(null)} className="md:hidden p-1 hover:bg-slate-100 rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
                    {activeConv.other_user?.picture ? <img src={activeConv.other_user.picture} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white font-bold text-sm">{activeConv.other_user?.name?.[0]}</div>}
                    <p className="font-semibold text-sm text-[#0F172A]">{activeConv.other_user?.name}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="messages-list">
                    {messages.map((m) => (
                      <div key={m.message_id} className={`flex ${m.from_user_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${m.from_user_id === user?.user_id ? "bg-[#0B6E4F] text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                          {m.content}
                          <p className={`text-[10px] mt-1 ${m.from_user_id === user?.user_id ? "text-white/60" : "text-slate-400"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="p-4 border-t border-slate-100">
                    <div className="flex gap-2">
                      <Input
                        data-testid="message-input"
                        placeholder="Type a message..."
                        value={newMsg}
                        onChange={(e) => setNewMsg(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
                        className="flex-1 h-11 rounded-xl bg-slate-50 border-slate-200"
                      />
                      <Button onClick={handleSend} data-testid="send-message-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-xl h-11 px-4">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-400 font-medium">Select a conversation</p>
                    <p className="text-sm text-slate-300 mt-1">or search for a user to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Messages() {
  return <ProtectedRoute><MessagesContent /></ProtectedRoute>;
}
